import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { HttpClient } from '@angular/common/http';
import { Observable, Subscription, interval } from 'rxjs';
import { take, switchMap } from 'rxjs/operators';
import { AppState } from '../app.reducer';
import { IOrder, Order } from '../shared/models/order.model';
import { ProductsService } from 'src/app/services/products.service';
import { SetOrderActions } from '../shared/reducers/order.actions';
import { EncoderService } from './encoder.service';
import Swal from 'sweetalert2';
import _ from 'lodash';
import { ShoppingCartService } from './shopping-cart.service';

@Injectable({
	providedIn: 'root'
})
export class CartProductValidatorService {
	private validationSubscription: Subscription;
	private isValidating = false;

	constructor(
		private store: Store<AppState>,
		private productService: ProductsService,
		private encoder: EncoderService,
		private shoppingCartService: ShoppingCartService
	) { }


	startCartValidation(): void {
		if (this.isValidating) {
			return;
		}

		this.isValidating = true;
		//console.log('Iniciando validación periódica del carrito');

		this.validateCartProducts();

		this.validationSubscription = interval(30000)
			.pipe(
				switchMap(() => this.store.select('order').pipe(take(1)))
			)
			.subscribe((orderState: any) => {
				if (orderState.order && orderState.order.commerces && orderState.order.commerces.length > 0) {
					this.validateCartProducts();
				}
			});
	}


	stopCartValidation(): void {
		if (this.validationSubscription) {
			this.validationSubscription.unsubscribe();
			this.validationSubscription = null;
		}
		this.isValidating = false;
		//console.log('Validación periódica del carrito detenida');
	}


	private validateCartProducts(): void {
		this.store.select('order').pipe(take(1)).subscribe((orderState: any) => {
			if (!orderState.order || !orderState.order.commerces) {
				return;
			}

			const order = new Order(orderState.order);
			let hasInactiveProducts = false;
			let hasPriceChanges = false;
			const inactiveProductNames: string[] = [];
			const priceChangedProductNames: string[] = [];
			const priceChangedProducts: Array<{ name: string; oldPrice: number; newPrice: number }> = [];
			const updatedOrder = JSON.parse(JSON.stringify(order));

			// Empareja cada commerce original con su clon en updatedOrder por referencia,
			// no por índice: cada respuesta HTTP llega de forma independiente y, si otra
			// respuesta ya eliminó un commerce vacío de updatedOrder.commerces, un índice
			// capturado de antemano apuntaría al commerce equivocado.
			const commerceRefMap = new Map(order.commerces.map((commerce, i) => [commerce, updatedOrder.commerces[i]]));

			const validationPromises = order.commerces.map((commerce) => {
				return new Promise<void>((resolve) => {
					if (!commerce.conceptos || commerce.conceptos.length === 0) {
						resolve();
						return;
					}

					this.productService.getProducts(commerce.commerce_key)
						.pipe(take(1))
						.subscribe({
							next: (products) => {
								const targetCommerce = commerceRefMap.get(commerce);
								const conceptsToRemove: number[] = [];

								commerce.conceptos.forEach((concept, conceptIndex) => {
									const currentProduct = _.find(products, ['key', concept.product_key]);

									if (!currentProduct || currentProduct.active === false) {
										hasInactiveProducts = true;
										inactiveProductNames.push(concept.titulo);
										conceptsToRemove.push(conceptIndex);
									} else {
										// Verificar y actualizar cambios de precio si aplica
										const currentSalePriceRaw = (currentProduct.oferta === true) ? currentProduct.precio_oferta : currentProduct.precio;
										const currentSalePrice = parseFloat(currentSalePriceRaw);
										if (!isNaN(currentSalePrice) && concept.precio_venta !== currentSalePrice) {
											hasPriceChanges = true;
											priceChangedProductNames.push(concept.titulo);
											priceChangedProducts.push({ name: concept.titulo, oldPrice: concept.precio_venta, newPrice: currentSalePrice });
											targetCommerce.conceptos[conceptIndex].precio_venta = currentSalePrice;
										}
									}
								});

								conceptsToRemove.reverse().forEach(index => {
									targetCommerce.conceptos.splice(index, 1);
								});

								if (targetCommerce.conceptos.length === 0) {
									const currentIndex = updatedOrder.commerces.indexOf(targetCommerce);
									if (currentIndex !== -1) {
										updatedOrder.commerces.splice(currentIndex, 1);
									}
								}

								resolve();
							},
							error: (error) => {
								// El logging no debe poder impedir el resolve(): un error de
								// red (CORS, timeout) puede traer error.error como un
								// ErrorEvent/ProgressEvent no serializable, y si JSON.stringify
								// lanza antes de resolve(), esta promesa quedaría colgada para
								// siempre y Promise.all() nunca resolvería.
								let rawBody: string;
								try {
									rawBody = typeof error?.error === 'string' ? error.error.slice(0, 1000) : JSON.stringify(error?.error);
								} catch {
									rawBody = String(error?.error);
								}
								console.error(
									'Error validando productos del comercio:', commerce.commerce_key,
									'| status:', error?.status,
									'| message:', error?.message,
									'| body:', rawBody
								);
								resolve();
							}
						});
				});
			});

				Promise.all(validationPromises).then(() => {
					if (hasInactiveProducts || hasPriceChanges) {
						this.shoppingCartService.updateTotal(updatedOrder);
						const action = new SetOrderActions(updatedOrder);
						this.store.dispatch(action);
						localStorage.setItem('order', this.encoder.encode(JSON.stringify(updatedOrder)));
						//console.log('Order updated:', updatedOrder);

						const removedList = inactiveProductNames.join(', ');
						const priceChangedDetailsHtml = priceChangedProducts.length > 0
							? `<ul style="text-align:left;">${priceChangedProducts.map(p => `<li><strong>${p.name}</strong>: ${p.oldPrice.toFixed(2)} → ${p.newPrice.toFixed(2)} €</li>`).join('')}</ul>`
							: '';

						if (hasInactiveProducts && !updatedOrder.available_for_purchase) {
							Swal.fire({
								title: 'Actualizaciones en tu cesta',
								html: `${removedList ? `Los siguientes productos ya no están disponibles y han sido eliminados de tu cesta:<br><br><strong>${removedList}</strong><br><br>` : ''}${hasPriceChanges ? `Los siguientes productos han cambiado de precio y se han actualizado en tu cesta:<br><br>${priceChangedDetailsHtml}<br>` : ''}Por favor, actualiza tu cesta para continuar con la compra.`,
								icon: 'info',
								confirmButtonText: 'Entendido'
							}).then(() => {
								window.location.href = '/comercios';
							});
						} else {
							Swal.fire({
								title: 'Actualizaciones en tu cesta',
								html: `${removedList ? `Los siguientes productos ya no están disponibles y han sido eliminados de tu cesta:<br><br><strong>${removedList}</strong><br><br>` : ''}${hasPriceChanges ? `Los siguientes productos han cambiado de precio y se han actualizado en tu cesta:<br><br>${priceChangedDetailsHtml}` : ''}`,
								icon: 'info',
								confirmButtonText: 'Entendido'
							});
						}
					}
				});
		});
	}


	validateNow(): void {
		this.validateCartProducts();
	}


	isValidatingCart(): boolean {
		return this.isValidating;
	}
}
